import {
  first,
  listOf,
  personName,
  query,
  request,
  toBool,
  toDay,
  toNumber,
  unwrap,
  versionNumber,
} from "../SalaryTemplates/salaryTemplateApi";
import { normalizeCategory } from "../SalaryComponents/salaryComponentApi";
import {
  arrayOf,
  findRows,
  idText,
  objectOf,
  personFields,
  cardsOf,
  readCardCounts,
  readStatus,
  uniqueBy,
} from "../AssignSalary/assignSalaryApi";

// Payroll > Salary > Employee Salary on the payroll/salary/ APIs (spec
// section 9). Read-only: employee-salary (list / single),
// employee-salary-history and employee-salary-compare.
//
// The API doc has no sample responses, so every reader below accepts the
// likely field names and falls back to safe defaults. Check them against a
// live response.

const BASE = "payroll/salary/";
const SALARY = `${BASE}employee-salary/`;

// Server status text -> Active | NotUsed | Upcoming.
const statusKey = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("not used") || text.includes("not_used") || text.includes("notused")) return "NotUsed";
  if (text.includes("upcoming")) return "Upcoming";
  return "Active";
};

const templateFields = (row) => {
  const template = objectOf(first(row.template, objectOf(row.current_salary).template));
  return {
    template_id: idText(first(row.template_id, row.current_template_id, template.id)),
    template_name: first(row.template_name, row.current_template_name, template.name) || "",
    template_version: versionNumber(first(row.template_version, row.current_template_version, template.version, template.version_number, template.version_no)),
  };
};

const structureOf = (row) =>
  toBool(first(row.is_customized, row.customized), /custom/i.test(String(textOf(first(row.structure_type, row.structure, row.structure_label)))));

// Text to show: a string as is, an object by its label / name (never the
// object itself, which React can't render).
export const textOf = (value) => {
  if (value == null) return "";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value)) return value.map(textOf).filter(Boolean).join(", ");
  return String(first(value.label, value.name, value.text, value.title, value.value, value.reason) ?? "");
};

// ---------- List ----------

export const fromEmployeeSalaryApi = (raw) => {
  const row = { ...objectOf(raw.current_salary), ...raw };
  const upcoming = objectOf(first(row.upcoming_change, row.upcoming, row.upcoming_salary));
  const status = objectOf(row.status);
  const key = statusKey(textOf(first(row.status, row.salary_status, row.status_label)) || first(status.key, status.label));
  // last_change: text, or { reason, change, change_pct, month }.
  const lastChange = objectOf(row.last_change);
  const lastPct = toNumber(first(lastChange.change_pct, lastChange.change_percent, row.last_change_percent, row.last_change_pct, row.change_percent));
  return {
    ...personFields(row),
    ...templateFields(row),
    template_name: textOf(templateFields(row).template_name),
    position: textOf(first(row.designation_name, row.position, row.designation)),
    pay_basis: textOf(first(row.salary_type, row.pay_basis, row.pay_frequency)) || "Monthly",
    customized: structureOf(row),
    gross: toNumber(first(row.gross, row.current_gross, row.gross_salary)),
    effective_from: toDay(first(row.effective_from, row.effective_date, row.current_effective_from)),
    last_change: textOf(first(lastChange.reason, typeof row.last_change === "string" ? row.last_change : undefined, row.last_change_reason, row.last_reason, row.reason)),
    last_change_date: toDay(first(lastChange.date, row.last_change_date, row.last_revised, row.last_revised_on)),
    last_change_pct: lastPct,
    // "+8% · Apr 2026" from the object's parts when the server sends them.
    last_change_detail:
      textOf(row.last_change_detail) ||
      [textOf(lastChange.change) || (lastPct != null ? `${lastPct >= 0 ? "+" : ""}${lastPct}%` : ""), textOf(lastChange.month)].filter(Boolean).join(" · "),
    status_key: key,
    status_detail: textOf(first(row.status_detail, row.status_subline, row.status_note, row.status_remarks, status.detail)),
    upcoming: Object.keys(upcoming).length
      ? { gross: toNumber(first(upcoming.gross, upcoming.new_gross)), from: toDay(first(upcoming.effective_from, upcoming.from)) }
      : null,
    has_open_revision: toBool(first(row.has_open_revision, row.has_pending_revision, row.has_open_request, row.pending_revision), false),
    first_payroll: textOf(first(row.first_payroll, row.first_used_in_payroll, row.first_payroll_month)),
  };
};

const isSalaryRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  const fields = personFields(row);
  return Boolean(fields.id && fields.name);
};

// List + card counts (All, Active, Active - Not Used, Upcoming change).
export const listEmployeeSalaries = async (domain, companyId, signal) => {
  const data = await request(domain, query(SALARY, { company_id: companyId }), { signal });
  if (!data || typeof data !== "object") throw new Error("The server returned an invalid employee salary list.");
  const value = unwrap(data);
  const list = listOf(data, ["employees", "salaries", "employee_salaries"]) || findRows(value, isSalaryRow);
  const counts = readCardCounts(cardsOf(data));
  const pick = (...keys) => keys.map((key) => counts[key]).find((count) => count != null) ?? null;
  return {
    employees: uniqueBy(list.filter(isSalaryRow).map(fromEmployeeSalaryApi), (row) => row.id),
    counts: {
      all: pick("all", "total"),
      Active: pick("active"),
      NotUsed: pick("active_not_used", "not_used", "active_notused"),
      Upcoming: pick("upcoming_change", "upcoming"),
    },
  };
};

// ---------- Single (Salary Details tab) ----------

const amountRow = (entry, category) => ({
  code: String(first(entry.code, entry.component_code, entry.component_id, entry.name) ?? ""),
  name: textOf(first(entry.payslip_name, entry.name, entry.component_name, entry.code)),
  category: normalizeCategory(textOf(first(entry.category, entry.component_category))) || category,
  amount: toNumber(first(entry.amount, entry.monthly_amount, entry.value, entry.computed_amount)),
});

// A pending revision or approved-later salary, for the banner and history.
const fromPendingApi = (item) => {
  const { status, level } = readStatus(first(item.status, item.request_status), item);
  const upcoming = status === "Approved" || /upcoming/i.test(String(first(item.kind, item.type, item.status) || ""));
  return {
    kind: upcoming ? "Upcoming" : "Proposed",
    status: upcoming ? "Approved" : status,
    level,
    number: textOf(first(item.request_no, item.request_number, item.number)),
    reason: textOf(first(item.reason, item.revision_reason)),
    change: textOf(first(item.change_text, item.summary, item.change)),
    gross: toNumber(first(item.new_gross, item.gross, item.revised_gross)),
    from: toDay(first(item.effective_from, item.from)),
  };
};

export const getEmployeeSalary = async (domain, userId, signal) => {
  const data = unwrap(await request(domain, query(SALARY, { user_id: userId }), { signal }));
  const raw = Array.isArray(data) ? data[0] : first(data?.employee, data?.salary) || data;
  if (!raw || typeof raw !== "object") throw new Error("The server returned an invalid employee salary.");
  const row = { ...objectOf(raw.header), ...objectOf(raw.strip), ...objectOf(raw.current_salary), ...raw };
  // The salary's rows and totals sit under details (Salary Details tab).
  const breakdown = objectOf(first(row.breakdown, row.salary_breakdown, row.details));
  const totals = objectOf(breakdown.totals);
  const rowsAll = arrayOf(first(row.components, row.rows, breakdown.components, breakdown.rows));
  const earnings = arrayOf(first(row.earnings_rows, Array.isArray(row.earnings) ? row.earnings : undefined, breakdown.earnings))
    .map((entry) => amountRow(entry, "Earnings"));
  const deductions = arrayOf(first(row.deductions_rows, Array.isArray(row.deductions) ? row.deductions : undefined, breakdown.deductions))
    .map((entry) => amountRow(entry, "Deductions"));
  const employer = arrayOf(breakdown.employer_contributions).map((entry) => amountRow(entry, "Employer Contribution"));
  const reimbursements = arrayOf(breakdown.reimbursements).map((entry) => amountRow(entry, "Reimbursement"));
  const fromAll = rowsAll.map((entry) => amountRow(entry, ""));
  const allEarnings = earnings.length ? earnings : fromAll.filter((entry) => entry.category === "Earnings" || entry.category === "Additional Earnings");
  const allDeductions = deductions.length ? deductions : fromAll.filter((entry) => entry.category === "Deductions" || entry.category === "Additional Deductions");
  const sum = (rows) => rows.reduce((total, entry) => total + (entry.amount || 0), 0);
  const record = objectOf(first(row.salary_record, row.record, breakdown.salary_record));
  const statutory = arrayOf(first(row.statutory, row.statutory_hints, row.statutory_eligibility))
    .map((item) =>
      typeof item === "string"
        ? { label: item, value: "" }
        : { label: first(item.label, item.name, item.code) || "", value: String(first(item.value, item.status, item.eligibility) ?? "") },
    )
    .filter((item) => item.label);
  const approvers = arrayOf(first(record.approvals, row.approvals, record.approved_by_list));
  // A pending request and an approved salary that starts later (single
  // objects from the server).
  const single = (value) => (value && typeof value === "object" && !Array.isArray(value) ? [value] : undefined);
  const pending = arrayOf(first(row.pending_revisions, single(row.pending_revision), single(row.pending), row.upcoming_changes, row.future))
    .concat(arrayOf(single(row.upcoming)))
    .filter((item) => item && typeof item === "object")
    .map(fromPendingApi);
  const grossTotal = toNumber(first(row.total_earnings, breakdown.total_earnings, row.gross_total, totals.gross)) ?? (allEarnings.length ? sum(allEarnings) : toNumber(first(row.gross, row.current_gross)));
  const deductionTotal = toNumber(first(row.total_deductions, breakdown.total_deductions, totals.deductions)) ?? sum(allDeductions);
  return {
    ...fromEmployeeSalaryApi(row),
    earnings: allEarnings,
    deductions: allDeductions,
    employer,
    reimbursements,
    gross_total: grossTotal,
    additional_total: toNumber(totals.additional_earnings) ?? sum(allEarnings.filter((entry) => entry.category === "Additional Earnings")),
    deductions_total: deductionTotal,
    employer_total: toNumber(totals.employer_contribution) ?? sum(employer),
    ctc: toNumber(totals.ctc_before_statutory),
    net: toNumber(first(row.net_before_statutory, breakdown.net_before_statutory, totals.net_before_statutory, row.net)) ?? (grossTotal || 0) - deductionTotal,
    statutory: statutory.length ? statutory : null,
    pending,
    record: {
      version: versionNumber(first(record.version, record.version_no, record.salary_version, row.salary_version, row.version)),
      reason: textOf(first(record.reason, record.source, row.reason)),
      number: textOf(first(record.request_no, record.request_number, record.number, row.request_no)),
      approved_by:
        first(
          typeof record.approved_by === "string" ? record.approved_by : undefined,
          approvers.length ? approvers.map((item) => (typeof item === "string" ? item : `${personName(first(item.name, item.by, item.approved_by, item.actor)) || "–"}${item.level ? ` (L${item.level})` : ""}`)).join(" · ") : undefined,
          personName(record.approved_by),
        ) || "",
      first_payroll: textOf(first(record.first_used_in_payroll, record.first_payroll, toDay(record.first_payroll_end_date), row.first_used_in_payroll, row.first_payroll)),
    },
  };
};

// ---------- History ----------

const historyKind = (value, item) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("propos") || text.includes("pending")) return "Proposed";
  if (text.includes("upcoming")) return "Upcoming";
  if (text.includes("current") || toBool(item.is_current, false)) return "Current";
  return "Past";
};

export const fromHistoryEntry = (item) => {
  const kind = historyKind(first(item.kind, item.type, item.state, item.label, item.status), item);
  const { status, level } = readStatus(first(item.request_status, kind === "Proposed" ? item.status : undefined), item);
  return {
    // Structure id: what employee-salary-compare takes.
    id: idText(first(item.structure_id, item.salary_structure_id, item.id)),
    kind,
    request_status: kind === "Proposed" ? status : kind === "Upcoming" ? "Approved" : "",
    level,
    version: versionNumber(first(item.version, item.version_no, item.salary_version, item.version_number)),
    gross: toNumber(first(item.gross, item.new_gross, item.gross_salary)),
    reason: textOf(first(item.reason, item.source, item.change_reason)),
    detail: textOf(first(item.detail, item.note, item.remarks)),
    change_pct: toNumber(first(item.change_percent, item.change_pct, item.percent_change)),
    template_name: textOf(first(item.template_name, objectOf(item.template).name)),
    template_version: versionNumber(first(item.template_version, objectOf(item.template).version, objectOf(item.template).version_no)),
    customized: structureOf(item),
    from: toDay(first(item.effective_from, item.from, item.from_date)),
    to: toDay(first(item.effective_to, item.to, item.to_date)),
    number: textOf(first(item.request_no, item.request_number, item.number)),
    approved_by: first(
      typeof item.approved_by === "string" ? item.approved_by : undefined,
      arrayOf(item.approvals).map((entry) => (typeof entry === "string" ? entry : personName(first(entry.name, entry.by)))).filter(Boolean).join(", ") || undefined,
    ) || "",
  };
};

const ORDER = { Proposed: 0, Upcoming: 1, Current: 2, Past: 3 };

// Proposed, Upcoming, Current, Past; newest first.
export const getEmployeeSalaryHistory = async (domain, userId, signal) => {
  const data = await request(domain, query(`${BASE}employee-salary-history/`, { user_id: userId }), { signal });
  const value = unwrap(data);
  let list = listOf(data, ["history", "versions", "timeline"]);
  if (!list && value && typeof value === "object") {
    // Grouped answer: { proposed: [...], upcoming: [...], current: {...}, past: [...] }.
    list = ["proposed", "upcoming", "current", "past"].flatMap((group) =>
      arrayOf(Array.isArray(value[group]) ? value[group] : value[group] ? [value[group]] : []).map((item) => ({ kind: group, ...item })),
    );
  }
  return arrayOf(list)
    .filter((item) => item && typeof item === "object")
    .map(fromHistoryEntry)
    .sort((a, b) => ORDER[a.kind] - ORDER[b.kind] || String(b.from).localeCompare(String(a.from)));
};

// ---------- Compare ----------

const side = (value) => {
  const item = objectOf(value);
  return {
    version: versionNumber(first(item.version, item.version_no, item.salary_version)),
    from: toDay(first(item.effective_from, item.from)),
    template: [first(item.template_name, objectOf(item.template).name), first(item.template_version, objectOf(item.template).version, objectOf(item.template).version_no)]
      .filter((part) => part != null && part !== "")
      .map((part, index) => (index === 1 ? `V${String(part).replace(/^v/i, "")}` : part))
      .join(" "),
    reason: textOf(first(item.reason, item.source)),
    gross: toNumber(first(item.gross, item.total_earnings)),
    net: toNumber(first(item.net_before_statutory, item.net)),
  };
};

const compareRow = (entry, category) => {
  const before = toNumber(first(entry.a, entry.a_amount, entry.old_amount, entry.before, entry.from_amount, entry.amount_a));
  const after = toNumber(first(entry.b, entry.b_amount, entry.new_amount, entry.after, entry.to_amount, entry.amount_b));
  const changeText = String(first(entry.change, entry.status, entry.change_type, entry.type) || "").toLowerCase();
  return {
    code: String(first(entry.code, entry.component_code, entry.name) ?? ""),
    name: textOf(first(entry.payslip_name, entry.name, entry.component_name, entry.code)),
    category: normalizeCategory(textOf(first(entry.category, entry.component_category))) || category,
    a: before,
    b: after,
    diff: toNumber(first(entry.difference, entry.diff, entry.change_amount)) ?? (before != null && after != null ? after - before : null),
    change: changeText.includes("add") ? "Added" : changeText.includes("remov") ? "Removed" : before == null && after != null ? "Added" : after == null && before != null ? "Removed" : "",
  };
};

const pair = (value, fallbackA, fallbackB) => {
  const item = objectOf(value);
  const a = toNumber(first(item.a, item.before, item.old, fallbackA));
  const b = toNumber(first(item.b, item.after, item.new, fallbackB));
  return { a, b, diff: toNumber(first(item.difference, item.diff)) ?? (a != null && b != null ? b - a : null) };
};

// Two versions (structure ids from the history).
export const compareEmployeeSalary = async (domain, userId, a, b, signal) => {
  const value = unwrap(await request(domain, query(`${BASE}employee-salary-compare/`, { user_id: userId, a, b }), { signal }));
  const data = objectOf(value);
  const header = objectOf(data.header);
  const left = side(first(data.a, data.version_a, data.older, data.from, header.before));
  const right = side(first(data.b, data.version_b, data.newer, data.to, header.after));
  const rows = [
    ...arrayOf(first(data.earnings, data.earnings_rows)).map((entry) => compareRow(entry, "Earnings")),
    ...arrayOf(first(data.deductions, data.deductions_rows)).map((entry) => compareRow(entry, "Deductions")),
  ];
  const all = rows.length ? rows : arrayOf(first(data.components, data.rows)).map((entry) => compareRow(entry, ""));
  const totals = objectOf(data.totals);
  return {
    left,
    right,
    earnings: all.filter((row) => row.category === "Earnings" || row.category === "Additional Earnings"),
    deductions: all.filter((row) => row.category === "Deductions" || row.category === "Additional Deductions"),
    gross: pair(first(data.gross, totals.gross), left.gross, right.gross),
    net: pair(first(data.net, data.net_before_statutory, totals.net, totals.net_before_statutory), left.net, right.net),
    template_changed: toBool(first(data.template_changed, data.is_template_changed, objectOf(header.after).template_changed), left.template !== right.template),
  };
};

